// Tab lifecycle: new / close / select / reorder + internal (settings) tabs.
import { state, blankTab, active } from "./state.js";
import { setAddr } from "./dom.js";
import { render, dropFrame, frames } from "./view.js";
import { leaveLinuxOk } from "./linux-open.js";
import { applyRestore } from "./session-store.js";
import { startWatch } from "./errors.js";
import { metric } from "../shared/metric.js";
import { trackOpen } from "./quests.js";

// Which feature an in-app page is, for the private counter (fixed names only).
function pageMetric(url) {
	const p = String(url).split(/[?#]/)[0];
	const m = /^\/tools\/([a-z]+)$/.exec(p);
	if (m) return "tool:" + m[1];
	return { "/tools": "tools", "/linux": "linux", "/hall": "hall", "/settings": "settings" }[p] || "";
}

export function newTab() {
	const id = state.next++;
	state.tabs.push(blankTab(id));
	state.activeId = id;
	setAddr("");
	render();
	return id;
}

// Open a same-origin internal page (e.g. /settings) directly - not proxied.
export function openInternal(url, title) {
	const id = state.next++;
	const t = blankTab(id);
	t.internal = url;
	t.title = title || "Page";
	t.loading = true; // show the loader until the frame loads (fast pages never flash it)
	t.focusOnLoad = true;
	t.skipLoader = false;
	state.tabs.push(t);
	state.activeId = id;
	setAddr("");
	render();
	startWatch(id); // catch a stalled/missing internal page (game/tool) with the error overlay
	const name = pageMetric(url);
	if (name) metric(name);
	trackOpen(url);
	return id;
}

// hash: where to open Settings: a section ("#browsing"), a row ("#set=panicKey") or an old anchor
// ("#bell"); see shared/settings-index.js resolveHash (SETTINGS_HASH there matches this one). An open
// Settings tab is brought forward and told where to go.
const SETTINGS_HASH = /^#(?:[a-z-]{1,30}|set=[A-Za-z0-9]{1,40})$/;
export function openSettings(hash) {
	const h = typeof hash === "string" && SETTINGS_HASH.test(hash) ? hash : "";
	const existing = state.tabs.find((t) => t.internal && t.internal.split("#")[0] === "/settings");
	if (existing) {
		state.activeId = existing.id;
		setAddr("");
		render();
		const f = frames.get(existing.id);
		if (h && f) { try { f.contentWindow.postMessage({ type: "revault-settings-goto", hash: h }, location.origin); } catch { /* still loading */ } }
		return existing.id;
	}
	return openInternal("/settings" + h, "Settings");
}

export function closeTab(id) {
	const i = state.tabs.findIndex((t) => t.id === id);
	if (i < 0) return;
	const t = state.tabs[i];
	if (!leaveLinuxOk(t)) return; // a running Linux machine, or something unsaved, would be lost
	if (t && (t.url || t.internal || t.view)) {
		state.closed.push({ url: t.url, internal: t.internal, view: t.view, title: t.title });
		if (state.closed.length > 25) state.closed.shift();
	}
	state.tabs.splice(i, 1);
	dropFrame(id);
	if (state.tabs.length === 0) { const nid = state.next++; state.tabs.push(blankTab(nid)); state.activeId = nid; }
	else if (state.activeId === id) state.activeId = state.tabs[state.tabs.length - 1].id;
	const a = active();
	setAddr(a ? a.addr : "");
	render();
}

export function selectTab(id) {
	state.activeId = id;
	const a = active();
	if (a && a.restore) { applyRestore(a); return; } // lazily load a restored tab on first select
	setAddr(a ? a.addr : "");
	render();
}

// Pin / batch-close (used by the tab context menu). Pinned tabs are skipped by
// the batch closers and keep no ✕ button.
export function pinTab(id) {
	const t = state.tabs.find((x) => x.id === id);
	if (t) { t.pinned = !t.pinned; render(); }
}
export function closeOthers(id) {
	state.tabs.filter((t) => t.id !== id && !t.pinned).map((t) => t.id).forEach(closeTab);
}
export function closeRight(id) {
	const i = state.tabs.findIndex((t) => t.id === id);
	if (i < 0) return;
	state.tabs.slice(i + 1).filter((t) => !t.pinned).map((t) => t.id).forEach(closeTab);
}

export function reorder(from, to) {
	if (from == null || from === to) return;
	const fi = state.tabs.findIndex((t) => t.id === from);
	if (fi < 0) return;
	const moved = state.tabs.splice(fi, 1)[0];
	let ti = state.tabs.findIndex((t) => t.id === to);
	if (ti < 0) ti = state.tabs.length;
	state.tabs.splice(ti, 0, moved);
	render();
}

// Keyboard and menu alternative to dragging: move a tab one place left (-1) or right (+1).
export function moveTab(id, dir) {
	const i = state.tabs.findIndex((t) => t.id === id);
	const j = i + dir;
	if (i < 0 || j < 0 || j >= state.tabs.length) return false;
	const [moved] = state.tabs.splice(i, 1);
	state.tabs.splice(j, 0, moved);
	state.activeId = id;
	render();
	return true;
}

export function onDragStart(e, id) { state.dragId = id; if (e.dataTransfer) e.dataTransfer.effectAllowed = "move"; }
export function onDrop(e, id) { e.preventDefault(); const from = state.dragId; state.dragId = null; reorder(from, id); }
